import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, CreditCard, AlertCircle, Loader2, RefreshCw, CheckCircle, X,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainNavigation from '../components/MainNavigation';
import ThemeToggle from '../components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [creditPackages, setCreditPackages] = useState([]);
  const [creditInfo, setCreditInfo] = useState({ never_expire_credits: 0, expiry_history: [] });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const [pendingSessionId, setPendingSessionId] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    fetchTransactions();
    fetchCreditPackages();
    fetchPlans();
    reconcilePendingPayments();

    let sessionId = searchParams.get('session_id');
    if (sessionId) {
      sessionId = sessionId.replace(/[{}]/g, '');
      setPendingSessionId(sessionId);
      checkPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await axios.get(`${API_URL}/api/payment/credit-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('獲取套餐失敗:', error);
      setPlans([
        { id: 'small', name: '入門套餐', price: 10, credits: 100, currency: 'HKD', validity_days: 30 },
        { id: 'medium', name: '進階套餐', price: 50, credits: 500, currency: 'HKD', validity_days: 60 },
        { id: 'large', name: '專業套餐', price: 100, credits: 1000, currency: 'HKD', validity_days: 90 },
      ]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchCreditPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/credit-packages`);
      setCreditPackages(response.data.packages || []);
      setCreditInfo({
        never_expire_credits: response.data.never_expire_credits || 0,
        expiry_history: response.data.expiry_history || [],
      });
      if (response.data.expired_amount > 0) {
        toast.warning(`有 ${response.data.expired_amount} 點數已到期並已扣除`, { duration: 8000 });
        await refreshUser();
      }
    } catch (error) {
      console.error('獲取積分套餐失敗:', error);
    }
  };

  const reconcilePendingPayments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payment/reconcile`);
      if (response.data.updated > 0) {
        await refreshUser();
        fetchCreditPackages();
        toast.success('已完成補發點數處理');
      }
    } catch (error) {
      console.error('Reconcile error:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error('獲取交易記錄失敗:', error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    setPaymentResult(null);

    try {
      const response = await axios.get(`${API_URL}/api/payment/status/${sessionId}`);
      if (response.data.status === 'complete') {
        setPaymentResult('success');
        toast.success(`充值成功！已添加 ${response.data.credits_added} 點數`);
        await refreshUser();
        await fetchTransactions();
        await fetchCreditPackages();
        setSearchParams({});
        setPendingSessionId(null);
      } else if (response.data.payment_status === 'paid') {
        setPaymentResult('success');
        toast.success('付款成功！點數即將添加');
        await refreshUser();
        await fetchTransactions();
        await fetchCreditPackages();
        setSearchParams({});
        setPendingSessionId(null);
      } else {
        setPaymentResult('pending');
        toast.info('付款尚未完成，請在 Stripe 完成付款後再點擊刷新');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setPaymentResult('failed');
        toast.error('找不到此交易記錄，請聯繫客服');
      } else {
        setPaymentResult('pending');
        toast.error('檢查狀態失敗，請稍後再試');
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleManualRefresh = () => {
    if (pendingSessionId) checkPaymentStatus(pendingSessionId);
  };

  const handleClearPending = () => {
    setPendingSessionId(null);
    setPaymentResult(null);
    setSearchParams({});
  };

  const handleRecharge = async (packageId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API_URL}/api/payment/create-checkout-session`, {
        package_id: packageId,
        origin_url: originUrl,
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || '創建支付會話失敗');
      setLoading(false);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('zh-TW', { year: 'numeric', month: 'short', day: 'numeric' });

  const getDaysUntilExpiry = (expiryDate) => {
    const diffTime = new Date(expiryDate) - new Date();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getExpiryBadge = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days <= 0) return { cls: 'text-red-700 bg-red-50 border-red-200', text: '已過期' };
    if (days <= 7) return { cls: 'text-orange-700 bg-orange-50 border-orange-200', text: `${days}天後過期` };
    if (days <= 30) return { cls: 'text-amber-700 bg-amber-50 border-amber-200', text: `${days}天後過期` };
    return { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', text: `${days}天後過期` };
  };

  const getCurrencySymbol = (currency) => {
    const s = { HKD: 'HK$', USD: '$', TWD: 'NT$', CNY: '¥' };
    return s[currency?.toUpperCase()] || '$';
  };

  if (!user) return null;

  return (
    <div data-atelier="true" className="h-screen flex overflow-hidden bg-cream-150 pb-14 md:pb-0">
      <MainNavigation />

      <div
        data-theme="cream"
        className="flex-1 flex flex-col overflow-hidden surface-cream"
        data-testid="profile-page"
      >
        {/* Top neon strip (brand continuity, thin) */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-lime-500/40 to-transparent flex-shrink-0" />

        {/* Header */}
        <header className="surface-glass-cream border-b border-cream-300 px-4 md:px-12 py-4 flex-shrink-0 relative z-10">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/create')}
              className="rounded-md text-cream-500 hover:text-cream-900 hover:bg-cream-200 h-8 px-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
              <span className="text-sm">返回</span>
            </Button>
            <span className="h-4 w-px bg-cream-300" />
            <span className="label-micro text-cream-500">ACCOUNT</span>
            <h1 className="font-serif text-base font-semibold text-cream-900 tracking-tight">個人中心</h1>
            <div className="ml-auto">
              <ThemeToggle />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-4 md:px-12 py-10 md:py-16">

            {/* Payment status banner */}
            {pendingSessionId && (
              <div className="mb-10 border border-cream-300 bg-white rounded-xl p-5">
                <div className="flex items-start md:items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    {checkingStatus ? (
                      <Loader2 className="w-6 h-6 text-cream-900 animate-spin flex-shrink-0" />
                    ) : paymentResult === 'success' ? (
                      <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-serif font-semibold text-cream-900 text-base">
                        {checkingStatus ? '正在檢查付款狀態…'
                          : paymentResult === 'success' ? '付款成功！'
                          : paymentResult === 'failed' ? '找不到交易'
                          : '等待付款確認'}
                      </h3>
                      <p className="text-sm text-cream-700 mt-0.5">
                        {checkingStatus ? '請稍候'
                          : paymentResult === 'success' ? '點數已添加到您的帳戶'
                          : paymentResult === 'failed' ? '請聯繫客服處理'
                          : '如已在 Stripe 完成付款，請點擊刷新按鈕'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleManualRefresh}
                      disabled={checkingStatus}
                      className="rounded-full bg-cream-900 hover:bg-cream-700 text-cream-50 text-sm h-8 px-4"
                      data-testid="refresh-status-btn"
                    >
                      {checkingStatus ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                          刷新
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleClearPending}
                      variant="outline"
                      className="rounded-full text-sm h-8 px-4 border-cream-300 bg-transparent text-cream-700 hover:bg-cream-200"
                    >
                      關閉
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* HERO: Name + credit headline */}
            <section className="mb-16 md:mb-20">
              <div className="label-micro text-cream-500 mb-5 flex items-center gap-3">
                <span className="h-px w-8 bg-cream-400" />
                WELCOME BACK
              </div>

              <div className="grid md:grid-cols-12 gap-10 items-end">
                <div className="md:col-span-7">
                  <h1 className="font-serif font-semibold text-cream-900 tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-3">
                    {user.name || 'Creator'}
                  </h1>
                  <p className="text-cream-700 text-base leading-relaxed">
                    {user.email}
                    <span className="mx-2 text-cream-400">·</span>
                    <span className="label-micro text-cream-500">{user.role?.toUpperCase() || 'USER'}</span>
                  </p>
                </div>
                <div className="md:col-span-5 md:text-right" data-testid="profile-credits">
                  <div className="label-micro text-cream-500 mb-2">AVAILABLE CREDITS</div>
                  <div className="font-display font-semibold text-cream-900 text-7xl md:text-8xl tabular-nums tracking-tighter leading-none">
                    {Math.floor(user.credits)}
                  </div>
                </div>
              </div>
              <div className="mt-10 h-px bg-cream-300" />
            </section>

            {/* Recharge packages — hero CTA row */}
            <section className="mb-16">
              <div className="flex items-end justify-between mb-7">
                <div>
                  <div className="label-micro text-cream-500 mb-3 flex items-center gap-3">
                    <span className="h-px w-8 bg-cream-400" />
                    RECHARGE · CREDITS
                  </div>
                  <h2 className="font-serif font-semibold text-cream-900 text-2xl md:text-3xl tracking-tight">
                    充值套餐
                  </h2>
                </div>
              </div>

              {loadingPlans ? (
                <div className="py-12 text-center text-cream-500 text-sm">載入中…</div>
              ) : (
                <div className="grid md:grid-cols-3 gap-px bg-cream-300 border border-cream-300 rounded-xl overflow-hidden">
                  {plans.map((pkg, i) => {
                    const highlighted = i === 1; // middle featured
                    return (
                      <div
                        key={pkg.id}
                        className={`p-8 md:p-10 flex flex-col ${highlighted ? 'bg-cream-900 text-cream-50' : 'bg-white'}`}
                      >
                        <div className={`label-micro mb-4 ${highlighted ? 'text-lime-500' : 'text-cream-500'}`}>
                          PLAN · {String(i + 1).padStart(2, '0')}
                        </div>
                        <h3 className={`font-serif font-semibold text-xl mb-2 ${highlighted ? 'text-cream-50' : 'text-cream-900'}`}>
                          {pkg.name}
                        </h3>

                        <div className="mt-4 flex items-baseline gap-1">
                          <span className={`text-sm ${highlighted ? 'text-cream-300' : 'text-cream-500'}`}>
                            {getCurrencySymbol(pkg.currency)}
                          </span>
                          <span className={`font-display font-semibold text-5xl md:text-6xl tabular-nums tracking-tighter leading-none ${highlighted ? 'text-cream-50' : 'text-cream-900'}`}>
                            {pkg.price}
                          </span>
                        </div>

                        <div className={`mt-5 space-y-1.5 text-sm ${highlighted ? 'text-cream-100' : 'text-cream-700'}`}>
                          <div className="flex items-center justify-between">
                            <span className="opacity-70">點數</span>
                            <span className="font-mono tabular-nums">{pkg.credits}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="opacity-70">有效期</span>
                            <span className="font-mono tabular-nums">{pkg.validity_days} 天</span>
                          </div>
                        </div>

                        <Button
                          onClick={() => handleRecharge(pkg.id)}
                          disabled={loading}
                          className={`mt-8 w-full rounded-full h-11 text-sm font-semibold transition-all ${
                            highlighted
                              ? 'bg-lime-500 hover:bg-lime-400 text-obsidian-900'
                              : 'bg-cream-900 hover:bg-cream-700 text-cream-50'
                          }`}
                          data-testid={`recharge-${pkg.id}-btn`}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          立即充值
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* My active packages */}
            {(creditPackages.length > 0 || creditInfo.never_expire_credits > 0 || creditInfo.expiry_history.length > 0) && (
              <section className="mb-16" data-testid="credit-packages-section">
                <div className="label-micro text-cream-500 mb-3 flex items-center gap-3">
                  <span className="h-px w-8 bg-cream-400" />
                  ACTIVE · PACKAGES
                </div>
                <h2 className="font-serif font-semibold text-cream-900 text-2xl md:text-3xl tracking-tight mb-6">
                  我的積分套餐
                </h2>

                <div className="border-t border-cream-300">
                  {creditPackages.map((pkg, i) => {
                    const badge = pkg.expiry_date
                      ? getExpiryBadge(pkg.expiry_date)
                      : { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', text: '無限期' };
                    return (
                      <div
                        key={pkg.id || i}
                        className="border-b border-cream-300 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 items-center"
                        data-testid={`credit-package-row-${i}`}
                      >
                        <div>
                          <div className="label-micro text-cream-500 mb-1">REMAINING</div>
                          <div className="font-display font-semibold text-cream-900 text-3xl tabular-nums tracking-tight">
                            {Math.floor(pkg.remaining)}
                          </div>
                        </div>
                        <div>
                          <div className="label-micro text-cream-500 mb-1">PURCHASED</div>
                          <div className="text-sm text-cream-900 font-mono tabular-nums">
                            {pkg.purchase_date ? formatDate(pkg.purchase_date) : '—'}
                          </div>
                        </div>
                        <div>
                          <div className="label-micro text-cream-500 mb-1">EXPIRES</div>
                          <div className="text-sm text-cream-900 font-mono tabular-nums">
                            {pkg.expiry_date ? formatDate(pkg.expiry_date) : '永不過期'}
                          </div>
                        </div>
                        <div className="flex justify-start md:justify-end">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium border ${badge.cls}`}>
                            {badge.text}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {creditInfo.never_expire_credits > 0 && (
                    <div
                      className="border-b border-cream-300 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 items-center"
                      data-testid="credit-package-row-never-expire"
                    >
                      <div>
                        <div className="label-micro text-cream-500 mb-1">REMAINING</div>
                        <div className="font-display font-semibold text-cream-900 text-3xl tabular-nums tracking-tight">
                          {Math.floor(creditInfo.never_expire_credits)}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <div className="label-micro text-cream-500 mb-1">SOURCE</div>
                        <div className="text-sm text-cream-900">註冊贈送 / 管理員調整</div>
                      </div>
                      <div className="flex justify-start md:justify-end">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium border text-emerald-700 bg-emerald-50 border-emerald-200">
                          無限期
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {creditPackages.some(
                  (pkg) => pkg.expiry_date && getDaysUntilExpiry(pkg.expiry_date) <= 7 && getDaysUntilExpiry(pkg.expiry_date) > 0
                ) && (
                  <div className="mt-5 border border-amber-300 bg-amber-50 rounded-xl px-4 py-3 flex items-center gap-2.5">
                    <AlertCircle className="w-4 h-4 text-amber-700" strokeWidth={1.75} />
                    <span className="text-amber-900 text-sm">您有積分即將過期，請盡快使用！</span>
                  </div>
                )}

                {creditInfo.expiry_history.length > 0 && (
                  <div className="mt-6 border border-red-200 bg-red-50 rounded-xl px-4 py-4" data-testid="credit-expiry-history">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-4 h-4 text-red-700" strokeWidth={1.75} />
                      <span className="text-red-900 text-sm font-medium">點數到期扣除記錄</span>
                    </div>
                    <div className="space-y-2">
                      {creditInfo.expiry_history.map((h, i) => (
                        <div key={i} className="flex items-start justify-between gap-4 text-sm" data-testid={`credit-expiry-item-${i}`}>
                          <div className="min-w-0">
                            <div className="text-red-900">{h.note}</div>
                            <div className="label-micro text-red-700/70 mt-0.5">
                              {new Date(h.created_at).toLocaleString('zh-TW')}
                            </div>
                          </div>
                          <div className="font-mono tabular-nums text-red-700 font-semibold whitespace-nowrap">
                            {Number(h.amount).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-red-800/80 mt-3">
                      套餐點數於有效期屆滿當日自動到期並一次過扣除，不會再計入餘額。如有疑問請聯絡客服。
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* Transaction history */}
            <section>
              <div className="label-micro text-cream-500 mb-3 flex items-center gap-3">
                <span className="h-px w-8 bg-cream-400" />
                TRANSACTION · HISTORY
              </div>
              <h2 className="font-serif font-semibold text-cream-900 text-2xl md:text-3xl tracking-tight mb-6">
                充值記錄
              </h2>

              {transactions.length === 0 ? (
                <div className="border-t border-b border-cream-300 py-14 text-center">
                  <p className="text-cream-500 text-sm">暫無充值記錄</p>
                </div>
              ) : (
                <div className="border-t border-cream-300" data-testid="transaction-list">
                  {transactions.map((txn) => (
                    <div
                      key={txn.id}
                      className="border-b border-cream-300 py-4 flex items-center justify-between gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono tabular-nums text-cream-900 text-sm font-medium">
                          {getCurrencySymbol(txn.currency)}{txn.amount?.toFixed(2)}
                        </div>
                        <div className="label-micro text-cream-500 mt-1">
                          {new Date(txn.created_at).toLocaleString('zh-TW')}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono tabular-nums text-cream-900 text-sm font-medium">
                          +{txn.credits}
                        </div>
                        <div
                          className={`label-micro mt-1 ${
                            txn.payment_status === 'paid' ? 'text-emerald-700' : 'text-amber-700'
                          }`}
                        >
                          {txn.payment_status === 'paid' ? '已完成' : '處理中'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;
